import { ButtonProps } from "@mui/material";
import { RouteButton } from "@/components/common/styles";
import React from "react";
import useCustomizeQuery, {
  UseCustomizeArgs,
} from "./newcustomize/hooks/useCustomizeQuery";
import usePlanId from "@/hooks/usePlanId";
import getProxyPlanKey from "@/utils/getProxyPlanKey";
import usePlan from "@/hooks/usePlan";

type Props = ButtonProps & {
  customizeData: Omit<UseCustomizeArgs, "existingPlanId"> & {
    /**
     * Null means it's a new plan, undefined means it's the default plan
     *
     * Null might be useful when you want to link "Buy this plan again" where a plan has been cancelled
     */
    existingPlanId?: UseCustomizeArgs["existingPlanId"] | null;
  };
};

export default function CustomizeButton({ customizeData, ...props }: Props) {
  const { existingPlanId } = customizeData;
  const urlPlanId = usePlanId();
  // As a convenience we assume we want to use planId from the URL, but sometimes this is not desired
  // So as an option we allow you to pass in "null" which forces the undefined planId, which changes
  // the underlying behaviour of useCustomizeQuery
  const finalPlanId =
    typeof existingPlanId === "undefined"
      ? urlPlanId
      : existingPlanId || undefined;

  const { data: plan, isLoading: isPlanLoading } = usePlan(
    finalPlanId || "default"
  );

  const finalProxyPlan =
    customizeData.proxyPlan ?? (plan ? getProxyPlanKey(plan) : undefined);

  const { link, isLoading } = useCustomizeQuery({
    ...customizeData,
    existingPlanId: finalPlanId,
    proxyPlan: finalProxyPlan,
  });
  return (
    <RouteButton
      {...link}
      loading={isLoading || isPlanLoading}
      disabled={isLoading}
      {...props}
    />
  );
}
